/**
 * AdminCleanup — Database audit & sync repair tool.
 *
 * Solves the production issue where the admin grid and the public landing
 * page diverged because of:
 *   - Duplicate drafts from failed publishes ("LAGOS" appearing 3x, etc.)
 *   - "Untitled" placeholder posts from empty-slot uploads where the
 *     admin never went back to fill in metadata.
 *   - Archived items lingering and cluttering the grid.
 *
 * Single-page UI that calls GET /api/admin/projects/audit on mount,
 * shows the counts, and lets the admin run targeted cleanup actions.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function AdminCleanup() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/projects/audit");
      setAudit(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load audit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const cleanup = async (body, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setRunning(true);
    try {
      const { data } = await api.post("/admin/projects/cleanup", body);
      const total = data.total_deleted;
      toast.success(
        total
          ? `Cleanup complete — ${total} record${total === 1 ? "" : "s"} deleted.`
          : "Nothing to clean — DB already tidy."
      );
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Cleanup failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div data-testid="admin-cleanup" className="max-w-4xl">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>

      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-slate-950">Database Cleanup & Sync Audit</h1>
          <p className="text-slate-500 text-sm mt-1.5 leading-relaxed max-w-2xl">
            If your admin grid doesn't match the live landing page, this tool will find duplicate drafts, orphan "Untitled" posts, and archived clutter, and let you remove them in one tap. The persistent localStorage cache also auto-invalidates after any change so the public site converges immediately.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-sm border border-slate-200 hover:bg-slate-50 px-3 py-1.5 rounded-md"
          data-testid="cleanup-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm">Loading audit…</div>
      ) : audit ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            <StatCard label="Total projects" value={audit.total} testId="audit-stat-total" />
            <StatCard label="Published (visible to visitors)" value={audit.published} tone="ok" testId="audit-stat-published" />
            <StatCard label="Drafts (admin-only)" value={audit.drafts} tone={audit.drafts > 0 ? "warn" : undefined} testId="audit-stat-drafts" />
            <StatCard label="Archived" value={audit.archived} tone={audit.archived > 0 ? "warn" : undefined} testId="audit-stat-archived" />
            <StatCard label="Untitled placeholders" value={audit.untitled} tone={audit.untitled > 0 ? "warn" : undefined} testId="audit-stat-untitled" />
            <StatCard label="Duplicate groups" value={audit.duplicate_groups} hint={`${audit.duplicate_extras} extras`} tone={audit.duplicate_groups > 0 ? "danger" : undefined} testId="audit-stat-duplicates" />
          </div>

          {audit.duplicates_preview?.length > 0 && (
            <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 mb-5" data-testid="audit-duplicates-preview">
              <div className="flex items-center gap-2 text-amber-900 font-medium text-sm mb-2">
                <AlertTriangle className="w-4 h-4" /> Duplicate groups found
              </div>
              <div className="space-y-1.5 text-[13px]">
                {audit.duplicates_preview.map((g, i) => (
                  <div key={i} className="text-amber-900/90">
                    <strong className="font-medium">{g.location || "(no location)"}</strong> — {g.count} copies
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <h2 className="font-heading text-lg font-medium text-slate-950 px-5 py-4 border-b border-slate-200">Cleanup actions</h2>
            <ActionRow
              title="Remove duplicates (recommended)"
              desc="Keep the newest, delete older copies that share location + first-media URL."
              count={audit.duplicate_extras}
              testId="cleanup-dedupe"
              onClick={() => cleanup({ dedupe: true }, `Delete ${audit.duplicate_extras} duplicate(s)? This cannot be undone.`)}
              disabled={running || audit.duplicate_extras === 0}
            />
            <ActionRow
              title='Delete "Untitled" placeholder posts'
              desc='Posts where location is blank, "Untitled", or "New Project" — usually from empty-slot uploads where details were never filled in.'
              count={audit.untitled}
              testId="cleanup-untitled"
              onClick={() => cleanup({ delete_untitled: true }, `Delete ${audit.untitled} Untitled post(s)? This cannot be undone.`)}
              disabled={running || audit.untitled === 0}
            />
            <ActionRow
              title="Delete all drafts"
              desc="Every is_published=false project. Use this only after reviewing — drafts may be intentional."
              count={audit.drafts}
              testId="cleanup-drafts"
              tone="danger"
              onClick={() => cleanup({ delete_drafts: true }, `Delete ALL ${audit.drafts} draft(s)? This cannot be undone.`)}
              disabled={running || audit.drafts === 0}
            />
            <ActionRow
              title="Delete archived"
              desc="Every is_archived=true project."
              count={audit.archived}
              testId="cleanup-archived"
              onClick={() => cleanup({ delete_archived: true }, `Delete ${audit.archived} archived post(s)? This cannot be undone.`)}
              disabled={running || audit.archived === 0}
            />
          </div>

          {audit.total === audit.published && audit.duplicate_groups === 0 && audit.untitled === 0 && (
            <div className="mt-5 flex items-center gap-2 text-emerald-700 text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-3.5">
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
              <span>Your database is clean — admin and frontend are in perfect sync.</span>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function StatCard({ label, value, hint, tone, testId }) {
  const toneCls =
    tone === "ok" ? "border-emerald-200 bg-emerald-50/40 text-emerald-900" :
    tone === "warn" ? "border-amber-200 bg-amber-50/40 text-amber-900" :
    tone === "danger" ? "border-red-200 bg-red-50/40 text-red-900" :
    "border-slate-200 bg-white text-slate-900";
  return (
    <div data-testid={testId} className={`border rounded-lg px-4 py-3 ${toneCls}`}>
      <div className="text-[11px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-heading font-medium mt-1">{value}</div>
      {hint && <div className="text-[11px] opacity-70 mt-0.5">{hint}</div>}
    </div>
  );
}

function ActionRow({ title, desc, count, onClick, disabled, tone, testId }) {
  const btnCls = tone === "danger"
    ? "bg-red-600 hover:bg-red-700 text-white"
    : "bg-slate-900 hover:bg-slate-800 text-white";
  return (
    <div className="border-t border-slate-200 px-5 py-4 flex items-start gap-4 first:border-t-0">
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-950 text-sm">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{desc}</div>
      </div>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        data-testid={testId}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap ${btnCls}`}
      >
        <Trash2 className="w-3.5 h-3.5" /> Delete {count > 0 ? count : ""}
      </button>
    </div>
  );
}
